'use client';
import { Transition } from '@headlessui/react';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { Button } from '@/components/button';
import { ContentfulIcon } from '@/components/icon';
import { Text } from '@/components/text';
import { getPageUrl } from '@/lib/utils/get-page-url';

import { CustomLink } from '../../custom-link';

import type { Page } from '@/app/generated/graphql';
import { ANALYTICS_EVENTS } from '@/constants';

type Props = {
  title: string;
  quickLinks?: Page[];
};
export const HeaderDropdown = ({ title, quickLinks }: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const pathname = usePathname();

  return (
    <li
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <Button
        variant="navigation"
        size="sm"
        onClick={() => setIsHovered(true)}
        className="cursor-default"
      >
        {title}
      </Button>
      <Transition
        show={Boolean(isHovered)}
        enter="transition-opacity duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ul className="absolute left-0 z-20 w-[22rem] pt-2">
          <div className="rounded-lg bg-white drop-shadow-lg">
            <div className="px-c pb-a pt-c">
              <Text
                variant="body2"
                weight="semi"
              >
                Quick Links
              </Text>
            </div>
            {quickLinks?.map((page, index) => (
              <li
                key={page.slug}
                onBlur={() => {
                  // Close the dropdown when tabbing out of the last link
                  if (index === quickLinks.length - 1) {
                    setIsHovered(false);
                  }
                }}
                className="last:rounded-b-lg hover:bg-gray-50"
              >
                <CustomLink
                  href={getPageUrl(page)}
                  confirmationRequired
                  onClick={() => {
                    trackAnalyticsEvent(ANALYTICS_EVENTS.headerDropdown.selected, {
                      currentLocation: pathname,
                      destination: getPageUrl(page),
                      menuCategory: title,
                    });

                    setIsHovered(false);
                  }}
                  className="button flex w-full items-center px-e py-d text-sm sm:px-d sm:py-c lg:px-c lg:py-b"
                >
                  {page.icon?.id ? (
                    <ContentfulIcon
                      className="mr-a h-icon w-icon text-pink-500"
                      id={page.icon.id}
                    />
                  ) : null}
                  {page.title}
                </CustomLink>
              </li>
            ))}
          </div>
        </ul>
      </Transition>
    </li>
  );
};
